import {
  Questionnaire,
  QuestionnaireItem,
  USER_ACTIVITY,
  UserAttributes,
} from '../../types';
import { apiClient } from '../api-client';
import {
  FeedbackPayload,
  MemberDetail,
  QuestionnairePayload,
  QuestionnaireResponse,
  QuestionnaireV3Payload,
  SearchChoice,
  UserAttributeData,
} from './type';
import { getBinahKey, getLocale, getProfileId } from 'lib/store';
import { getCurrentTimestamp } from 'lib/utils';
import useAuthStore from 'store/auth';

export const getMedsiQuestionnaire = async (params?: {
  user_history?: boolean;
  profile_id: string;
}) => {
  return await apiClient.get<Questionnaire[]>(
    `/medsi-questionnaire?os=web&locale=${getLocale()}`,
    { params }
  );
};

export const postMedsiQuestionnaire = async (data: QuestionnairePayload[]) => {
  return await apiClient.post(
    `/medsi-questionnaire?os=web&locale=${getLocale()}&profile_id=${getProfileId()}`,
    { data, profile_id: getProfileId() }
  );
};

export const updateMedsiQuestionnaire = async (
  data: QuestionnairePayload[]
) => {
  return await apiClient.patch(
    `/medsi-questionnaire?os=web&locale=${getLocale()}&profile_id=${getProfileId()}`,
    { data, profile_id: getProfileId() }
  );
};

// USER ATTRIBUTES
export const getUserAttributes = async (params?: { profile_id: string }) => {
  return await apiClient.get<{ user_attributes: UserAttributes }>(
    `/user-attributes?os=web&locale=${getLocale()}`,
    { params }
  );
};

export const postUserAttributes = async (
  data: UserAttributeData,
  profile_id?: string
) => {
  return await apiClient.post(
    `/user-attributes?os=web&locale=${getLocale()}${
      profile_id ? '&profile_id=' + profile_id : ''
    }`,
    data
  );
};

export const updateUserAttributes = async (
  data: Partial<UserAttributeData>,
  user_id?: string
) => {
  const profile_id = useAuthStore.getState().currentUserProfileId;
  return await apiClient.patch(
    `/user-attributes?os=web&locale=${getLocale()}${
      profile_id ? '&profile_id=' + profile_id : ''
    }`,
    { ...data, profile_id, ...(user_id && { user_id }) }
  );
};

export const updateFamilyAttributes = async (
  data: Partial<UserAttributeData>
) => {
  const profile_id = useAuthStore.getState().currentUserProfileId;
  return await apiClient.patch(
    `/family-member-attributes?os=web&locale=${getLocale()}`,
    { ...data, profile_id }
  );
};

// MEMBER DETAILS
export const getMemberDetails = async () => {
  const profile_id = useAuthStore.getState().currentUserProfileId;
  return (await apiClient.get(
    `/get-member-details?os=web&locale=${getLocale()}${
      profile_id ? '&profile_id=' + profile_id : ''
    }`
  )) as MemberDetail[];
};

// FAMILY MEMEBER ATTRIBUTES
export const getFamilyMemberAttributes = async (params?: {
  profile_id: string;
}) => {
  return (await apiClient.get(
    `/family-member-attributes?os=web&locale=${getLocale()}`,
    {
      params,
    }
  )) as UserAttributes;
};

export const postFamilyMemberAttributes = async (data: UserAttributeData) => {
  return await apiClient.post<UserAttributeData, { profile_id: string }>(
    `/family-member-attributes?os=web&locale=${getLocale()}`,
    data
  );
};

export const deleteFamilyMemberAttributes = async (ids: {
  profile_ids: string[];
}) => {
  return await apiClient.delete<{ message: string }>(
    `/family-member-attributes?os=web&locale=${getLocale()}`,
    { data: ids }
  );
};

// USER ACTIVITY
export const captureUserActivity = async (
  user_activity: USER_ACTIVITY,
  ...payload: any
) => {
  const profile_id = useAuthStore.getState().currentUserProfileId;
  return await apiClient.post(
    `/capture_user_activity?os=web&locale=${getLocale()}&activity_type=${user_activity}&client_name=medsi_check${
      profile_id ? '&profile_id=' + profile_id : ''
    }`,
    {
      binaah_key: getBinahKey(),
      timestamp: getCurrentTimestamp(),
      ...payload,
    }
  );
};

// FEEDBACK
export const postFeedback = async (data: FeedbackPayload) => {
  return await apiClient.post(
    `/user_feedback?os=web&profile_id=${getProfileId()}&client_name=medsi_check&locale=${getLocale()}`,
    data
  );
};

export const getQuestionnaireV3 = async (params: QuestionnaireV3Payload) => {
  return await apiClient.get<QuestionnaireResponse>('/medsi-questionnaire-v3', {
    params: {
      ...params,
      locale: getLocale(),
      os: 'web',
      profile_id: getProfileId(),
    },
  });
};

export const postQuestionnaireV3 = async ({
  question_sequence,
  ...data
}: QuestionnairePayload) => {
  return await apiClient.post('/medsi-questionnaire-v3', data, {
    params: {
      question_sequence: question_sequence.toString(),
      os: 'web',
      locale: getLocale(),
      profile_id: getProfileId(),
    },
  });
};

export const updateQuestionnaireV3 = async (data: QuestionnairePayload) => {
  return (await apiClient.patch('/medsi-questionnaire-v3', data, {
    params: {
      os: 'web',
      locale: getLocale(),
      profile_id: getProfileId(),
    },
  })) as { has_follow_up: boolean; data: [QuestionnaireItem] | null };
};

export const getQuestionnaireOptionSearchV3 = async ({
  code,
  search_text = '',
}: {
  code: string;
  search_text?: string;
}) => {
  return await apiClient.get<{
    eng_choices: SearchChoice[];
    spanish_choices: SearchChoice[];
  }>('/medsi-questionnaire-v3-option-search', {
    params: {
      locale: getLocale(),
      os: 'web',
      profile_id: getProfileId(),
      code,
      search_text,
    },
  });
};

export const postQuestionnaireOptionSearchV3 = async (data: {
  combos: string[];
}) => {
  return await apiClient.post('/medsi-questionnaire-v3-option-search', data, {
    params: {
      locale: getLocale(),
      os: 'web',
      profile_id: getProfileId(),
    },
  });
};
